// How this product looks to a customer: the shop grid card, and the page
// they reach by clicking it.
//
// The editor is a column of fields; the customer sees a square photo, a
// name and a price, then a page of options. Which image leads, what order
// the sizes appear in, whether a long name still reads — none of that is
// visible from the form, and checking meant saving and opening the shop in
// another tab.
//
// The grid half renders the real ProductCard, so it can't drift. The
// detail half is a scaled-down rendering of the same fields the product
// page uses: enough to check ordering, pricing and which options appear,
// not a second copy of that page's cart behaviour.
import React from 'react';
import { ChevronDown, EyeOff, Lock, PackageX } from 'lucide-react';
import type { Product } from '../../types';
import ProductCard from '../ProductCard';
import { formatCents } from '../../utils/money';

interface Props {
  product: Partial<Product>;
  /** Collections the shop has, for naming the one this belongs to. */
  libraryName?: string;
}

/** Why this wouldn't show on the public shop, if it wouldn't. */
const listingBlock = (
  product: Partial<Product>,
  libraryName?: string
): { icon: React.ReactNode; text: string } | undefined => {
  if (product.status !== 'active') {
    return {
      icon: <EyeOff size={14} />,
      text:
        product.status === 'archived'
          ? "Archived, so it isn't listed and its link returns a not-found page."
          : "A draft isn't listed, and its link returns a not-found page. Set it to Active to publish.",
    };
  }
  if (product.library_slug) {
    return {
      icon: <Lock size={14} />,
      text: `Only shown inside ${libraryName ?? product.library_slug}, to customers who have its password.`,
    };
  }
  return undefined;
};

/** One option row, rendered the way the product page renders it. */
const OptionRow: React.FC<{ label: string; values: string[] }> = ({ label, values }) => {
  if (values.length === 0) return null;
  return (
    <div>
      <div className="text-[11px] text-text-light mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {values.map(v => (
          <span
            key={v}
            className="border border-background-dark rounded px-2 py-0.5 text-[11px]"
          >
            {v}
          </span>
        ))}
      </div>
    </div>
  );
};

const ProductListingPreview: React.FC<Props> = ({ product, libraryName }) => {
  const blocked = listingBlock(product, libraryName);
  const soldOut = product.in_stock === false;
  const images = product.images ?? [];
  const sizes = product.size_options ?? [];

  // The page shows the override for the selected size; with none selected
  // it opens on the base price, and a cheaper size can make the total look
  // different from what the grid advertised.
  const overrides = product.size_price_overrides ?? {};
  const overridden = sizes.filter(s => overrides[s] !== undefined);

  return (
    <details className="bg-white border border-background-dark rounded group">
      <summary className="flex items-center gap-2 p-4 cursor-pointer list-none">
        <ChevronDown
          size={16}
          className="text-text-light transition group-open:rotate-180"
        />
        <span className="text-sm font-medium">Customer preview</span>
        <span className="text-xs text-text-light">
          {blocked ? 'not currently listed' : 'shop grid and product page'}
        </span>
      </summary>

      <div className="border-t border-background-dark p-4 space-y-4">
        <p className="text-xs text-text-light">
          Includes changes you haven't saved yet.
        </p>

        <div className="flex flex-wrap items-start gap-6">
          {/* Sized to roughly one grid cell. pointer-events-none because
              the card links to the public page, and a preview that
              navigated away mid-edit would be a trap. */}
          <div className="w-40 shrink-0 pointer-events-none select-none">
            <div className="text-[11px] text-text-light mb-2">On the shop grid</div>
            <ProductCard
              product={
                {
                  ...product,
                  // The card only reads these; a draft has no id yet.
                  product_id: product.product_id ?? 'preview',
                  name: product.name || 'Untitled product',
                  price_cents: product.price_cents ?? 0,
                  images,
                } as Product
              }
            />
          </div>

          <ul className="text-xs text-text-light space-y-2 min-w-0 flex-1">
            {blocked ? (
              <li className="flex items-start gap-2 text-amber-800">
                <span className="mt-0.5 shrink-0">{blocked.icon}</span>
                <span>{blocked.text}</span>
              </li>
            ) : (
              <li className="text-green-700">Listed on the public shop.</li>
            )}
            {soldOut && (
              <li className="flex items-start gap-2">
                <PackageX size={14} className="mt-0.5 shrink-0" />
                <span>
                  Marked out of stock: it still appears, with a Sold out
                  badge, and can't be added to a cart.
                </span>
              </li>
            )}
            {images.length === 0 && (
              <li>No photos yet — the grid shows an empty placeholder.</li>
            )}
            {images.length > 1 && (
              <li>The first of {images.length} photos is the cover.</li>
            )}
            {!product.price_cents && (
              <li>No price set, so the grid shows a dash instead of an amount.</li>
            )}
            {overridden.length > 0 && (
              <li>
                {overridden.join(', ')}{' '}
                {overridden.length === 1 ? 'has its own price' : 'have their own prices'},
                so the page total changes with the size chosen.
              </li>
            )}
          </ul>
        </div>

        <div className="border-t border-dashed border-background-dark pt-4">
          <div className="text-[11px] text-text-light mb-3">
            After clicking into it
          </div>

          <div className="flex flex-wrap gap-5">
            {/* The gallery, in the order set above: first is what opens. */}
            <div className="shrink-0">
              <div className="w-40 h-40 rounded border border-background-dark bg-background-dark overflow-hidden">
                {images[0] ? (
                  <img
                    src={images[0]}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-text-light">
                    No image
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-1 mt-1">
                  {images.slice(0, 5).map((url, i) => (
                    <img
                      key={url + i}
                      src={url}
                      alt=""
                      className="w-7 h-7 object-cover rounded border border-background-dark"
                    />
                  ))}
                  {images.length > 5 && (
                    <span className="text-[10px] text-text-light self-end">
                      +{images.length - 5}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <div className="text-base">{product.name || 'Untitled product'}</div>
                <div className="text-sm text-text-light">
                  {product.price_cents ? formatCents(product.price_cents) : '—'}
                </div>
              </div>

              {product.description && (
                <p className="text-xs text-text-light whitespace-pre-line line-clamp-3">
                  {product.description}
                </p>
              )}

              {soldOut && (
                <div className="text-xs text-red-600 uppercase tracking-wide">
                  Currently out of stock
                </div>
              )}

              <div className="space-y-2">
                <OptionRow label="Size" values={sizes} />
                <OptionRow label="Color" values={product.color_options ?? []} />
                <OptionRow
                  label="Thread color"
                  values={product.thread_color_options ?? []}
                />
                <OptionRow label="Font" values={product.font_options ?? []} />
              </div>

              {/* Offered on every product, whatever options are set. */}
              <div className="text-[11px] text-text-light">
                Personalization details — a free-text box the customer fills
                in{(product.personalization_fields ?? []).length > 0 &&
                  `, after ${(product.personalization_fields ?? []).length} custom field${
                    (product.personalization_fields ?? []).length === 1 ? '' : 's'
                  }`}
                .
              </div>

              <div
                className={`inline-block rounded px-3 py-1.5 text-xs ${
                  soldOut
                    ? 'bg-background-dark text-text-light'
                    : 'bg-primary text-white'
                }`}
              >
                {soldOut
                  ? 'Sold out'
                  : `Add to cart — ${formatCents(product.price_cents ?? 0)}`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </details>
  );
};

export default ProductListingPreview;
